import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import {
  getServicingDrawRequestsAction,
  getServicingSummaryAction,
} from 'store/loanApplications/actions';
import {
  selectServicingDrawRequests,
  selectServicingSummary,
} from 'store/loanApplications/selectors';

import { getDrawRequestsTableConfig } from '../Servicing.config';

import DrawRequestDrawer from './DrawRequestDrawer';
import { headerStyles } from './DrawRequests.styles';
import { DrawRequestsTestIds } from './DrawRequests.testIds';

const DrawRequests = ({ isLoading }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const drawRequests = useSelector(selectServicingDrawRequests);
  const summary = useSelector(selectServicingSummary);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingDrawRequest, setEditingDrawRequest] = useState(null);

  const onEdit = useCallback(row => {
    setEditingDrawRequest(row);
    setIsDrawerOpen(true);
  }, []);

  const tableConfig = useMemo(
    () => getDrawRequestsTableConfig({ onEdit, isLoading }),
    [onEdit, isLoading],
  );

  const onRequestDraw = useCallback(() => {
    setEditingDrawRequest(null);
    setIsDrawerOpen(true);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingDrawRequest(null);
  }, []);

  const onSuccess = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingDrawRequest(null);
    dispatch(getServicingSummaryAction({ applicationId }));
    dispatch(getServicingDrawRequestsAction({ applicationId }));
  }, [dispatch, applicationId]);

  return (
    <>
      <Box sx={headerStyles}>
        <Button
          data-testid={DrawRequestsTestIds.REQUEST_BUTTON}
          disabled={isLoading}
          onClick={onRequestDraw}
        >
          Request Draw
        </Button>
      </Box>

      <TableCDS
        withNoAvailableMessage
        config={tableConfig}
        data={drawRequests}
        isLoading={isLoading}
        skeletonRows={3}
      />

      <DrawRequestDrawer
        applicationId={applicationId}
        availableCredit={summary?.availableCredit}
        drawRequest={editingDrawRequest}
        isOpen={isDrawerOpen}
        onClose={onCloseDrawer}
        onSuccess={onSuccess}
      />
    </>
  );
};

DrawRequests.propTypes = {
  ...WithLoadingPropTypes,
};

export default memo(DrawRequests);
